<template>
  <div
    class="flex items-center gap-0.5 flex-wrap rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm px-1.5 py-1"
    :class="{ 'opacity-50 pointer-events-none': !editor }"
  >
    <button
      type="button"
      @mousedown.prevent
      @click="run((c) => c.undo())"
      class="p-1.5 rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-800 dark:hover:text-gray-100 transition-colors disabled:opacity-30"
      :disabled="!canUndo"
      :title="$t('presentation.tbUndo')"
    >
      <ArrowUturnLeftIcon class="w-4 h-4" />
    </button>
    <button
      type="button"
      @mousedown.prevent
      @click="run((c) => c.redo())"
      class="p-1.5 rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-800 dark:hover:text-gray-100 transition-colors disabled:opacity-30"
      :disabled="!canRedo"
      :title="$t('presentation.tbRedo')"
    >
      <ArrowUturnRightIcon class="w-4 h-4" />
    </button>

    <span class="w-px self-stretch bg-gray-200 dark:bg-gray-700 mx-1.5 my-0.5"></span>

    <TriadSelect
      :modelValue="fontValue"
      :options="fontOptions"
      :title="$t('presentation.tbFont')"
      @change="setFont"
      root-class="w-[96px]"
      button-class="justify-between bg-transparent hover:bg-gray-100 dark:hover:bg-gray-700 px-1.5 py-1.5 rounded-md border border-transparent text-xs font-medium text-gray-600 dark:text-gray-300"
      menu-class="w-full"
    />
    <TriadSelect
      :modelValue="sizeValue"
      :options="sizeOptions"
      :title="$t('presentation.tbSize')"
      @change="setSize"
      root-class="w-[64px]"
      button-class="justify-between bg-transparent hover:bg-gray-100 dark:hover:bg-gray-700 px-1.5 py-1.5 rounded-md border border-transparent text-xs font-medium text-gray-600 dark:text-gray-300"
      menu-class="w-full"
    />

    <span class="w-px self-stretch bg-gray-200 dark:bg-gray-700 mx-1.5 my-0.5"></span>

    <button
      type="button"
      @mousedown.prevent
      @click="run((c) => c.toggleBold())"
      class="w-7 h-7 rounded-md font-bold text-[15px] transition-colors"
      :class="isActive('bold') ? 'bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-800 dark:hover:text-gray-100'"
      :title="$t('presentation.tbBold')"
    >
      B
    </button>
    <button
      type="button"
      @mousedown.prevent
      @click="run((c) => c.toggleItalic())"
      class="w-7 h-7 rounded-md italic text-[15px] transition-colors"
      :class="isActive('italic') ? 'bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-800 dark:hover:text-gray-100'"
      :title="$t('presentation.tbItalic')"
    >
      I
    </button>
    <button
      type="button"
      @mousedown.prevent
      @click="run((c) => c.toggleUnderline())"
      class="w-7 h-7 rounded-md underline text-[15px] transition-colors"
      :class="isActive('underline') ? 'bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-800 dark:hover:text-gray-100'"
      :title="$t('presentation.tbUnderline')"
    >
      U
    </button>

    <span class="w-px self-stretch bg-gray-200 dark:bg-gray-700 mx-1.5 my-0.5"></span>

    <!-- Alinhamento Vertical do Elemento (Top, Middle, Bottom) -->
    <button
      type="button"
      @mousedown.prevent
      @click="emit('align', 'top')"
      class="p-1.5 rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-800 dark:hover:text-gray-100 transition-colors"
      :title="$t('presentation.tbAlignTop')"
    >
      <AlignStartHorizontal class="w-4 h-4" />
    </button>
    <button
      type="button"
      @mousedown.prevent
      @click="emit('align', 'middle')"
      class="p-1.5 rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-800 dark:hover:text-gray-100 transition-colors"
      :title="$t('presentation.tbAlignMiddle')"
    >
      <AlignCenterHorizontal class="w-4 h-4" />
    </button>
    <button
      type="button"
      @mousedown.prevent
      @click="emit('align', 'bottom')"
      class="p-1.5 rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-800 dark:hover:text-gray-100 transition-colors"
      :title="$t('presentation.tbAlignBottom')"
    >
      <AlignEndHorizontal class="w-4 h-4" />
    </button>

    <span class="w-px self-stretch bg-gray-200 dark:bg-gray-700 mx-1.5 my-0.5"></span>

    <!-- Alinhamento Horizontal do Elemento (Left, Center, Right) -->
    <button
      type="button"
      @mousedown.prevent
      @click="emit('align', 'left')"
      class="p-1.5 rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-800 dark:hover:text-gray-100 transition-colors"
      :title="$t('presentation.tbAlignLeft')"
    >
      <AlignStartVertical class="w-4 h-4" />
    </button>
    <button
      type="button"
      @mousedown.prevent
      @click="emit('align', 'center')"
      class="p-1.5 rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-800 dark:hover:text-gray-100 transition-colors"
      :title="$t('presentation.tbAlignCenter')"
    >
      <AlignCenterVertical class="w-4 h-4" />
    </button>
    <button
      type="button"
      @mousedown.prevent
      @click="emit('align', 'right')"
      class="p-1.5 rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-800 dark:hover:text-gray-100 transition-colors"
      :title="$t('presentation.tbAlignRight')"
    >
      <AlignEndVertical class="w-4 h-4" />
    </button>

    <span class="w-px self-stretch bg-gray-200 dark:bg-gray-700 mx-1.5 my-0.5"></span>

    <!-- Alinhamento de Texto -->
    <button
      type="button"
      @mousedown.prevent
      @click="setTextAlign('left')"
      class="p-1.5 rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-800 dark:hover:text-gray-100 transition-colors"
      :title="$t('presentation.tbTextAlignLeft')"
    >
      <AlignLeft class="w-4 h-4" />
    </button>
    <button
      type="button"
      @mousedown.prevent
      @click="setTextAlign('center')"
      class="p-1.5 rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-800 dark:hover:text-gray-100 transition-colors"
      :title="$t('presentation.tbTextAlignCenter')"
    >
      <AlignCenter class="w-4 h-4" />
    </button>
    <button
      type="button"
      @mousedown.prevent
      @click="setTextAlign('right')"
      class="p-1.5 rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-800 dark:hover:text-gray-100 transition-colors"
      :title="$t('presentation.tbTextAlignRight')"
    >
      <AlignRight class="w-4 h-4" />
    </button>

    <span class="w-px self-stretch bg-gray-200 dark:bg-gray-700 mx-1.5 my-0.5"></span>

    <button
      v-for="c in swatches"
      :key="c"
      type="button"
      @mousedown.prevent
      @click="run((ch) => ch.setColor(c))"
      class="w-5 h-5 rounded-full border transition-transform hover:scale-110"
      :class="isActive('textStyle', { color: c }) ? 'border-gray-800 dark:border-white ring-1 ring-gray-800 dark:ring-white' : 'border-gray-300 dark:border-gray-600'"
      :style="{ backgroundColor: c }"
      :title="c"
    ></button>
    <label
      class="w-5 h-5 rounded-full cursor-pointer border border-dashed border-gray-400 hover:border-gray-800 dark:hover:border-white transition-colors overflow-hidden"
      :title="$t('presentation.tbCustomColor')"
      @mousedown.prevent
    >
      <span class="block w-full h-full" style="background: conic-gradient(red, yellow, lime, aqua, blue, magenta, red)"></span>
      <input
        type="color"
        class="sr-only"
        @input="run((ch) => ch.setColor($event.target.value))"
      />
    </label>
    <button
      type="button"
      @mousedown.prevent
      @click="run((ch) => ch.unsetColor())"
      class="h-7 px-1 rounded-md text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
      :title="$t('presentation.tbClearColor')"
    >
      <span class="relative inline-block font-bold text-[13px] leading-none">A<XMarkIcon class="absolute -top-1 -right-2 w-2.5 h-2.5" /></span>
    </button>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, ref, watch, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { ArrowUturnLeftIcon, ArrowUturnRightIcon, XMarkIcon } from '@heroicons/vue/24/outline';
import {
  AlignStartVertical,
  AlignCenterVertical,
  AlignEndVertical,
  AlignStartHorizontal,
  AlignCenterHorizontal,
  AlignEndHorizontal,
  AlignLeft,
  AlignCenter,
  AlignRight,
} from 'lucide-vue-next';
import TriadSelect from '@/components/common/TriadSelect.vue';

const props = defineProps({
  editor: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['align', 'textAlign']);

const { t } = useI18n();

const swatches = ['#FFFFFF', '#FDE68A', '#F87171', '#34D399', '#60A5FA'];

const fontOptions = [
  { value: '', label: t('presentation.tbDefault') },
  { value: 'ui-sans-serif, system-ui, sans-serif', label: 'Sans' },
  { value: 'Georgia, "Times New Roman", serif', label: 'Serif' },
  { value: 'ui-monospace, monospace', label: 'Mono' },
];

const sizeOptions = [{ value: '', label: t('presentation.tbDefault') }].concat(
  [14, 18, 24, 32, 40, 48, 64, 80].map((s) => ({ value: `${s}px`, label: `${s}` }))
);

const fontValue = ref('');
const sizeValue = ref('');
const canUndo = ref(false);
const canRedo = ref(false);

const syncFromEditor = () => {
  if (!props.editor) {
    fontValue.value = '';
    sizeValue.value = '';
    canUndo.value = false;
    canRedo.value = false;
    return;
  }
  const attrs = props.editor.getAttributes('textStyle') || {};
  fontValue.value = attrs.fontFamily || '';
  sizeValue.value = attrs.fontSize || '';
  try {
    canUndo.value = props.editor.can().undo();
    canRedo.value = props.editor.can().redo();
  } catch {
    canUndo.value = false;
    canRedo.value = false;
  }
};

const onTransaction = () => syncFromEditor();

watch(
  () => props.editor,
  (ed, _, onCleanup) => {
    syncFromEditor();
    if (ed && ed.on) {
      ed.on('selectionUpdate', onTransaction);
      ed.on('transaction', onTransaction);
      onCleanup(() => {
        ed.off('selectionUpdate', onTransaction);
        ed.off('transaction', onTransaction);
      });
    }
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  if (props.editor && props.editor.off) {
    props.editor.off('selectionUpdate', onTransaction);
    props.editor.off('transaction', onTransaction);
  }
});

const run = (fn) => {
  if (!props.editor) return;
  const chain = props.editor.chain().focus();
  fn(chain);
  chain.run();
  syncFromEditor();
};

const isActive = (name, attrs) => {
  try {
    return !!props.editor && props.editor.isActive(name, attrs);
  } catch {
    return false;
  }
};

const setFont = (value) => {
  fontValue.value = value;
  run((ch) => (value ? ch.setFontFamily(value) : ch.unsetFontFamily()));
};

const setSize = (value) => {
  sizeValue.value = value;
  run((ch) => (value ? ch.setFontSize(value) : ch.unsetFontSize()));
};

const setTextAlign = (align) => {
  emit('textAlign', align);
  emit('align', align);
  if (props.editor && props.editor.chain) {
    try {
      props.editor.chain().focus().setTextAlign(align).run();
    } catch {
      // fallback handled by emit
    }
  }
};
</script>
